import {noop} from '@github-ui/noop'
import {Token as Parse5Token, Tokenizer} from 'parse5'
import type {Position} from 'unist'

interface Token {
  /** unist-compatible position of this token in the source string. */
  position: Position
  type: string
}

/** Anything that is not inside a tag. */
interface TextToken extends Token {
  type: 'text'
}

/** HTML comments, plus processing instructions (`<?...?>`) and CDATA sections (`<![CDATA[...]]>`). */
interface CommentToken extends Token {
  type: 'comment'
}

/** Start and self-closing tags. */
interface StartTagToken extends Token {
  type: 'startTag'
}

interface EndTagToken extends Token {
  type: 'endTag'
}

interface DoctypeToken extends Token {
  type: 'doctype'
}

export type Tokens = TextToken | CommentToken | StartTagToken | EndTagToken | DoctypeToken

export type TokenType = Tokens['type']

/**
 * Tokenize a partial HTML string. Does not care about matching tags or valid nesting, just breaks the string into
 * a flat array of tokens.
 */
export function tokenizeHtml(html: string) {
  const tokens: Tokens[] = []
  let lastToken: Tokens | null = null

  function handleToken(type: Tokens['type'], loc: Parse5Token.Location | null) {
    if (!loc) return

    const end = {
      column: loc.endCol,
      line: loc.endLine,
      offset: loc.endOffset,
    }

    // combine adjacent text nodes so we don't split on whitespace
    if (type === 'text' && lastToken?.type === 'text') {
      lastToken.position.end = end
      return
    }

    const token = {
      type,
      position: {
        start: {
          column: loc.startCol,
          line: loc.startLine,
          offset: loc.startOffset,
        },
        end,
      },
    }
    tokens.push(token)
    lastToken = token
  }

  const parser = new Tokenizer(
    {sourceCodeLocationInfo: true},
    {
      onCharacter: ({location}) => handleToken('text', location),
      onWhitespaceCharacter: ({location}) => handleToken('text', location),
      onNullCharacter: ({location}) => handleToken('text', location),
      onStartTag: ({location}) => handleToken('startTag', location),
      onEndTag: ({location}) => handleToken('endTag', location),
      onDoctype: ({location}) => handleToken('doctype', location),
      onComment: ({location}) => handleToken('comment', location),
      onEof: noop,
      onParseError: noop,
    },
  )

  parser.write(html, true)

  return tokens
}
